import axios from 'axios';
import md5 from 'js-md5';

class ApiClient {
  constructor(user) {
    this.user = user;
    this.baseURL = 'https://sandbox-api.heraclitus24.com/';
    switch(process.env.REACT_APP_STAGE) {
      case 'review': this.baseURL = 'https://sandbox-api.heraclitus24.com/'; break;
      case 'sandbox': this.baseURL = 'https://sandbox-api.heraclitus24.com/'; break;
      case 'staging': this.baseURL = 'https://staging-api.heraclitus24.com/'; break;
      case 'production': this.baseURL = 'https://api.heraclitus24.com/'; break;
      default: this.baseURL = 'https://sandbox-api.heraclitus24.com/'; break;
    }
    this.setAxiosInstanceWithoutHeaders();
  }
  
  setAxiosInstanceWithoutHeaders() {
    this.axiosInstance = axios.create({
      baseURL: this.baseURL
    });
    
    this.axiosInstance.interceptors.response.use(
      (resp) => {
        return resp.data;
      },
      (err) => {
        throw err;
      }
    );
  }
  
  setAxiosInstanceWithHeaders(headers) {
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      headers: headers
    });

    this.axiosInstance.interceptors.response.use(
      (resp) => {
        return resp.data;
      },
      (err) => {
        if (typeof err.response !== 'undefined' && err.response.status === 401) {
          this.user.signOut(window.location.reload());
        } else throw err;
      }
    );
  }
  
  addToken(authToken) {
    this.setAxiosInstanceWithHeaders({ "Authorization": `bearer ${authToken}`});
  }
  
  login(username, password) {
    const axiosConfig = {};
    axiosConfig.url = '/login';
    axiosConfig.method = 'post';
    axiosConfig.data = { 
      "username": username, 
      "password_md5": md5(password)
    };
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  patchUserPassword(userId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/users/'+userId+'/password';
    axiosConfig.method = 'patch';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getPsysets() {
    const axiosConfig = {};
    axiosConfig.url = '/psysets';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getPsysetDetails(psysetId, atlasId) {
    const axiosConfig = {};
    if (atlasId) axiosConfig.url = '/psysets/'+psysetId+'?atlas_id='+atlasId;
    else axiosConfig.url = '/psysets/'+psysetId;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  postPsyset(data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  patchPsyset(psysetId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId;
    axiosConfig.method = 'patch';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }

  postPsysetKeywords(psysetId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/keywords';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  patchPsysetKeywords(psysetId, keywordId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/keywords/'+keywordId;
    axiosConfig.method = 'patch';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  deletePsysetKeywords(psysetId, keywordId) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/keywords/'+keywordId;
    axiosConfig.method = 'delete';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  postPsysetRelation(psysetId, relationType, data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/'+relationType;
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  patchPsysetRelation(psysetId, relationType, relationId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/'+relationType+'/'+relationId;
    axiosConfig.method = 'patch';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  deletePsysetRelation(psysetId, relationType, relationId) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/'+relationType+'/'+relationId;
    axiosConfig.method = 'delete';
    axiosConfig.data = { symmetric: false };
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  postPsysetAsset(psysetId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/assets/';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  deletePsysetAsset(psysetId, assetId) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/assets/'+assetId;
    axiosConfig.method = 'delete';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getSynsets() {
    const axiosConfig = {};
    axiosConfig.url = '/synsets';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getSynsetsContaining(searchString, languageCode) {
    const axiosConfig = {};
    axiosConfig.url = '/synsets?contains='+searchString+'&language='+languageCode;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  postPsysetSynset(psysetId,data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/synsets';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  patchSynset(psysetId,synsetId,data) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/synsets/'+synsetId;
    axiosConfig.method = 'patch';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  deletePsysetSynset(psysetId,synsetId) {
    const axiosConfig = {};
    axiosConfig.url = '/psysets/'+psysetId+'/synsets/'+synsetId;
    axiosConfig.method = 'delete';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
    
  getSynsetDetails(synsetId) {
    const axiosConfig = {};
    axiosConfig.url = '/synsets/'+synsetId;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }

  getKeywords() {
    const axiosConfig = {};
    axiosConfig.url = '/keywords';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getKeywordDetails(keywordId) {
    const axiosConfig = {};
    axiosConfig.url = '/keywords/' + keywordId;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }

  postKeyword(data) {
    const axiosConfig = {};
    axiosConfig.url = '/keywords';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getAtlases() {
    const axiosConfig = {};
    axiosConfig.url = '/atlases';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  postAtlas(data) {
    const axiosConfig = {};
    axiosConfig.url = '/atlases';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getAtlasPsysets(atlasId, language) {
    const axiosConfig = {};
    axiosConfig.url = '/atlases/'+atlasId+'/psysets?language='+language;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }

  postAtlasPsyset(atlasId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/atlases/'+atlasId+'/psysets';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  deleteAtlasPsyset(atlasId, psysetId) {
    const axiosConfig = {};
    axiosConfig.url = '/atlases/'+atlasId+'/psysets/' + psysetId;
    axiosConfig.method = 'delete';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getAtlasCaptions(atlasId, languageCode) {
    const axiosConfig = {};
    axiosConfig.url = '/atlases/'+atlasId+'/captions?language='+languageCode;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  changeCellCaption(atlasId, data, method) {
    const axiosConfig = {};
    axiosConfig.url = '/atlases/'+atlasId+'/captions';
    axiosConfig.method = method;
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }

  getShapes() {
    const axiosConfig = {};
    axiosConfig.url = '/shapes';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getAssets() {
    const axiosConfig = {};
    axiosConfig.url = '/assets';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  postAsset(data) {
    const axiosConfig = {};
    axiosConfig.url = '/assets';
    axiosConfig.method = 'post';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  patchAsset(assetId, data) {
    const axiosConfig = {};
    axiosConfig.url = '/assets/'+assetId;
    axiosConfig.method = 'patch';
    axiosConfig.data = data;
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getLanguages() {
    const axiosConfig = {};
    axiosConfig.url = '/languages';
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
  
  getChain(data) {
    const axiosConfig = {};
    axiosConfig.url = '/tools/chains?word1=' + data.word1 + '&word2=' + data.word2 + '&emotion_words_only=' + data.emotion_words_only;
    axiosConfig.timeout = 60 * 2 * 1000;
    axiosConfig.method = 'get';
    axiosConfig.transformResponse = [ (response) => { return JSON.parse(response); } ];
    return this.axiosInstance(axiosConfig);
  }
}

export default ApiClient